/**
 * Workspace Layout Adapter – Phase 3B.
 *
 * Normalises BOTH legacy launcher layouts and workspace version layoutSchemas
 * into a single RuntimeLayoutData shape consumed by useLauncherLayout /
 * useLauncherResponsive.
 *
 * This is the compatibility bridge – the renderer does not care which source
 * produced the layout.
 *
 * DO NOT redesign the schema here. Compatibility-first.
 */
import { RUNTIME_SOURCE } from './workspaceResolverService';
import { LAUNCHER_GRID, clampGridColumns, clampGridRows } from '../../utils/launcherGrid';
export { RUNTIME_SOURCE };

/**
 * @typedef {Object} RuntimeLayoutData
 * @property {Array}   icons
 * @property {string}  theme
 * @property {number}  gridColumns
 * @property {number}  gridRows
 * @property {string}  iconSize
 * @property {number}  gap
 * @property {boolean} showGrid
 * @property {string}  backgroundImageUrl
 */

const DEFAULTS = Object.freeze({
  icons:              [],
  theme:              'system',
  gridColumns:        LAUNCHER_GRID.DEFAULT_COLUMNS,
  gridRows:           LAUNCHER_GRID.DEFAULT_ROWS,
  iconSize:           'medium',
  gap:                2,
  showGrid:           false,
  backgroundImageUrl: '',
});

/**
 * Coerce a raw icons value to a clean array.
 * Handles null / undefined / non-arrays safely.
 */
const safeIcons = (raw) => (Array.isArray(raw) ? raw : []);

/**
 * Apply DEFAULTS for any missing fields on an arbitrary layout object.
 */
const applyDefaults = (raw) => ({
  icons:              safeIcons(raw?.icons),
  theme:              raw?.theme              || DEFAULTS.theme,
  gridColumns:        clampGridColumns(raw?.gridColumns || DEFAULTS.gridColumns),
  gridRows:           clampGridRows(raw?.gridRows || DEFAULTS.gridRows),
  iconSize:           raw?.iconSize           || DEFAULTS.iconSize,
  gap:                raw?.gap                ?? DEFAULTS.gap,
  showGrid:           raw?.showGrid           ?? DEFAULTS.showGrid,
  backgroundImageUrl: raw?.backgroundImageUrl || DEFAULTS.backgroundImageUrl,
});

/**
 * Normalise a legacy launcher layout object.
 *
 * Input shape (from localStorage / GET /api/v2/users/me/prefs/layout):
 *   { icons: [...], theme, gridColumns, gridRows, iconSize, gap, showGrid }
 *
 * @param {object} rawLegacy
 * @returns {RuntimeLayoutData}
 */
export const normalizeLegacyLayout = (rawLegacy) => applyDefaults(rawLegacy);

/**
 * Normalise a workspace version's layoutSchema.
 *
 * Input shape (from GET /api/v2/workspaces/{id}/published → .layoutSchema):
 *   { icons: [...], theme, gridColumns, gridRows, iconSize, gap, showGrid }
 *
 * The schema intentionally mirrors the legacy shape for Phase 3B compatibility.
 *
 * @param {object} layoutSchema
 * @returns {RuntimeLayoutData}
 */
export const normalizeWorkspaceLayout = (layoutSchema) => applyDefaults(layoutSchema);

/**
 * Unified entry point.
 *
 * normalizeRuntimeLayout(runtimeSource, rawData) → RuntimeLayoutData
 *
 * Dispatches to the correct normaliser based on runtimeSource.
 * Any unknown source is treated as legacy for maximum safety.
 *
 * @param {'legacy'|'workspace'} runtimeSource
 * @param {object}               rawData
 * @returns {RuntimeLayoutData}
 */
export const normalizeRuntimeLayout = (runtimeSource, rawData) => {
  if (runtimeSource === RUNTIME_SOURCE.WORKSPACE) {
    return normalizeWorkspaceLayout(rawData);
  }
  return normalizeLegacyLayout(rawData);
};
